// Check if HTML string has valid text content
export function hasTextContent(htmlString: string) {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  const textContent = tempElement.textContent || tempElement.innerText;
  return textContent.trim().length > 0;
}

// Trims code block markers to avoid rendering issues
export function preProcessCodeBlock(content: string) {
  if (!content.trim().startsWith('```')) {
    content = '```\n' + content;
  }

  if (!content.trim().endsWith('```')) {
    content = content + '\n```';
  }
  return content;
}

// Helper function to get a display-friendly language name in the Markdown code block
export function getDisplayLanguage(language: string): string {
  const languageMap: Record<string, string> = {
    js: 'JavaScript',
    ts: 'TypeScript',
    jsx: 'React JSX',
    tsx: 'React TSX',
    py: 'Python',
    rb: 'Ruby',
    go: 'Go',
    java: 'Java',
    cs: 'C#',
    cpp: 'C++',
    rs: 'Rust',
    sh: 'Shell',
    bash: 'Bash',
    html: 'HTML',
    css: 'CSS',
    scss: 'SCSS',
    md: 'Markdown',
    json: 'JSON',
    yml: 'YAML',
    yaml: 'YAML',
    sql: 'SQL',
    javascript: 'JavaScript',
    typescript: 'TypeScript',
    python: 'Python',
    ruby: 'Ruby',
    golang: 'Go',
    csharp: 'C#',
    'c++': 'C++',
    cplusplus: 'C++',
    rust: 'Rust',
    shell: 'Shell',
    markdown: 'Markdown',
    text: 'Plain Text',
    '': 'Code',
  };

  return languageMap[language.toLowerCase()] || language || 'Code';
}

/*
Normalizes streaming markdown text with code blocks

Case 1: Adding missing closing backticks
Input: "Here's some code:\n```javascript\nconst x = 1;\n"
Output: "Here's some code:\n```javascript\nconst x = 1;\n```"

Case 2: Fixing incomplete backticks at the end
Input: "Here's some code:\n```javascript\nconst x = 1;\n``"
Output: "Here's some code:\n```javascript\nconst x = 1;\n```"

Case 3: Normalizing whitespace around code fences
Input: "Here's some code:\n```javascript\nconst x = 1;\n   ```"
Output: "Here's some code:\n```javascript\nconst x = 1;\n```"

Case 4: Removing empty code blocks
Input: "Text before\n```javascript\n   ```\nText after"
Output: "Text before\nText after"

Case 5: Handling odd number of backtick markers
Input: "Text ```code``` more text ```another code"
Output: "Text ```code``` more text "
*/
export const normalizeMarkdownCodeBlock = (rawOutput: string): string => {
  if (!rawOutput) return '';

  let processed = rawOutput;

  // Count fencing backticks and balance them if missing
  const openingFences = (processed.match(/```(\w*)\n?/g) || []).length;
  const closingFences = (processed.match(/\n?```\s*($|\n)/g) || []).length;
  // Adds missing closing markers
  if (openingFences > closingFences) {
    for (let i = 0; i < openingFences - closingFences; i++) {
      processed += '\n```';
    }
  }

  // Fixes Missing backticks
  if (processed.endsWith('``') && !processed.endsWith('```')) {
    processed += '`';
  }

  // Trim spaces around backticks
  processed = processed
    .replace(/\n\s+```/g, '\n```')
    .replace(/```\s+\n/g, '```\n')
    .replace(/```[a-zA-Z0-9]*\s*\n\s*```/g, '');

  // Remove empty code blocks
  const matches = processed.match(/```/g) || [];
  const count = matches.length;
  if (count % 2 !== 0) {
    // Find the last ``` marker and remove it along with preceding whitespace
    processed = processed.replace(/\s*```\s*$/, '');
  }

  return processed;
};
